import React, { useContext, useEffect, useState } from "react";
import { Input, Button, Tag, Empty, Spin, Tooltip } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import {
    EnvironmentOutlined,
    SearchOutlined,
    CloseOutlined,
    BankOutlined,
    LogoutOutlined,
    ArrowRightOutlined,
    SunOutlined,
    MoonOutlined,
    CheckCircleFilled,
} from "@ant-design/icons";

import { ThemeContext } from "../../contexts/ThemeContext";
import { urlHttpGov } from "../../services/urls";
import { ICity } from "../../interfaces";
import ImagemLogo from "../../assets/images/logo-lateral.png";
import useSelectCityModal from "./useSelectCityModal";
import "./style.css";

interface CityCardProps {
    city: ICity;
    isActive: boolean;
    onSelect: () => void;
    isSubmiting: boolean;
    isDark: boolean;
    index: number;
}

function CityCard({
    city,
    isActive,
    onSelect,
    isSubmiting,
    isDark,
    index,
}: CityCardProps) {
    const [imgError, setImgError] = useState(false);

    const logoFileName =
        city.logo_county ||
        (isDark ? city.logo_light || city.logo_dark : city.logo_light || city.logo_dark);

    const logoUrl = logoFileName && !imgError
        ? `${urlHttpGov}/logo_county/${logoFileName}`
        : null;

    const cityName = city.city || city.name || "Município";
    const uf = city.uf || "";

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                type: "spring",
                stiffness: 280,
                damping: 22,
                mass: 0.8,
                delay: Math.min(index * 0.04, 0.6),
            }}
            style={{ height: "100%" }}
        >
            <div
                className={`select-city-full-card ${isActive ? "is-active-card" : ""}`}
                onClick={onSelect}
                role="button"
                tabIndex={0}
            >
                <div className="select-city-full-card-top">
                    <div className="select-city-full-card-logo">
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt={`Brasão ${cityName}`}
                                onError={() => setImgError(true)}
                            />
                        ) : (
                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: isActive ? "#2563eb" : isDark ? "#38bdf8" : "#64748b",
                                }}
                            >
                                <BankOutlined style={{ fontSize: 28 }} />
                            </div>
                        )}
                    </div>

                    <div className="select-city-full-card-info">
                        <div className="select-city-full-card-name" title={`${cityName} - ${uf}`}>
                            {cityName} - {uf}
                        </div>
                        <div className="select-city-full-card-badges">
                            {isActive && (
                                <Tag
                                    color="green"
                                    icon={<CheckCircleFilled />}
                                    style={{
                                        margin: 0,
                                        borderRadius: 6,
                                        fontWeight: 700,
                                        fontSize: 11,
                                        padding: "1px 8px",
                                    }}
                                >
                                    Selecionado
                                </Tag>
                            )}
                        </div>
                    </div>
                </div>

                <div className="select-city-full-card-action">
                    <Button
                        type={isActive ? "primary" : "default"}
                        loading={isSubmiting}
                        className="select-city-full-card-btn"
                        style={{
                            borderRadius: 10,
                            background: isActive
                                ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                                : undefined,
                        }}
                    >
                        {isActive ? "Acessar Painel" : "Selecionar Município"}{" "}
                        <ArrowRightOutlined style={{ fontSize: 12 }} />
                    </Button>
                </div>
            </div>
        </motion.div>
    );
}

export default function SelectCityModal() {
    const {
        cities,
        filteredCities,
        searchQuery,
        setSearchQuery,
        isSubmiting,
        handleSelectCity,
        activeCity,
        isSelectCityModalOpen,
        handleCloseSelectCityModal,
        handleLogout,
    } = useSelectCityModal();

    const { theme, updateTheme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const canClose = Boolean(activeCity);

    // ESC key support
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isSelectCityModalOpen && canClose && !isSubmiting) {
                handleCloseSelectCityModal();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isSelectCityModalOpen, canClose, isSubmiting, handleCloseSelectCityModal]);

    if (!isSelectCityModalOpen) return null;

    return (
        <AnimatePresence>
            <div className={`select-city-fullscreen-overlay ${isDark ? "dark-mode" : ""}`}>
                {/* Full-screen Topbar */}
                <header className="select-city-topbar">
                    <div className="select-city-brand">
                        <img src={ImagemLogo} alt="Nota Município" />
                        <div className="select-city-brand-divider" />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span className="select-city-brand-title">Painel Administrativo</span>
                            <span style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 500 }}>
                                Seleção de Município
                            </span>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {/* Theme Toggle Button */}
                        <Tooltip title={isDark ? "Mudar para modo claro" : "Mudar para modo escuro"}>
                            <button
                                type="button"
                                className={`navbar-theme-toggle-btn ${isDark ? "dark" : ""}`}
                                onClick={() => updateTheme(isDark ? "light" : "dark")}
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                                    background: isDark ? "#1e293b" : "#f1f5f9",
                                }}
                            >
                                {isDark ? (
                                    <SunOutlined style={{ color: "#fbbf24", fontSize: 16 }} />
                                ) : (
                                    <MoonOutlined style={{ color: "#6366f1", fontSize: 16 }} />
                                )}
                            </button>
                        </Tooltip>

                        {/* Close button or Logout */}
                        {canClose ? (
                            <Tooltip title="Fechar (Esc)">
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={handleCloseSelectCityModal}
                                    style={{
                                        color: "#dc2626",
                                        background: "#fee2e2",
                                        border: "1px solid #fecaca",
                                        borderRadius: 8,
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Button
                                type="text"
                                danger
                                icon={<LogoutOutlined />}
                                onClick={handleLogout}
                                disabled={isSubmiting}
                                style={{
                                    borderRadius: 8,
                                    fontWeight: 600,
                                }}
                            >
                                Sair da conta
                            </Button>
                        )}
                    </div>
                </header>

                {/* Full-screen Scrollable Content */}
                <main className="select-city-scrollable-content">
                    <div className="select-city-container">
                        {/* Hero Header Section */}
                        <div className="select-city-hero">
                            <div className="select-city-hero-text">
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                    <EnvironmentOutlined style={{ fontSize: 22, color: "#2563eb" }} />
                                    <h1 style={{ margin: 0 }}>Selecione o Município</h1>
                                </div>
                                <p>
                                    Escolha qual município você deseja gerenciar no painel administrativo:
                                </p>
                            </div>

                            {/* Search Filter */}
                            {cities.length > 2 && (
                                <div className="select-city-hero-search">
                                    <Input
                                        placeholder="Pesquisar por nome ou UF..."
                                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        allowClear
                                        style={{
                                            borderRadius: 10,
                                            height: 42,
                                            fontSize: 13.5,
                                        }}
                                    />
                                    <Tag
                                        color="blue"
                                        style={{
                                            borderRadius: 6,
                                            padding: "4px 10px",
                                            fontSize: 12,
                                            fontWeight: 700,
                                            margin: 0,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {filteredCities.length} {filteredCities.length === 1 ? "município" : "municípios"}
                                    </Tag>
                                </div>
                            )}
                        </div>

                        {/* Cities Grid */}
                        {cities.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "60px 0" }}>
                                <Spin size="large" />
                                <div
                                    style={{
                                        marginTop: 16,
                                        color: isDark ? "#94a3b8" : "#64748b",
                                        fontSize: 14,
                                        fontWeight: 500,
                                    }}
                                >
                                    Carregando municípios disponíveis...
                                </div>
                            </div>
                        ) : filteredCities.length === 0 ? (
                            <div style={{ padding: "40px 0", textAlign: "center" }}>
                                <Empty
                                    description={
                                        <span style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 14 }}>
                                            Nenhum município encontrado para &quot;{searchQuery}&quot;
                                        </span>
                                    }
                                />
                            </div>
                        ) : (
                            <div className="select-city-grid">
                                {filteredCities.map((city, index) => (
                                    <CityCard
                                        key={city.id}
                                        city={city}
                                        isActive={activeCity?.id === city.id}
                                        onSelect={() => handleSelectCity(city)}
                                        isSubmiting={isSubmiting}
                                        isDark={isDark}
                                        index={index}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </AnimatePresence>
    );
}